import { useQuery, useQueryClient } from "@tanstack/react-query";
import { usePymbleApi } from "./usePymbleApi";
import _ from "lodash";

const DICTIONARY_END_POINT = "/dictionary/";

interface DictionaryEntry {
  Name?: string;
  Value?: string;
  Title?: string;
  [key: string]: unknown;
}

interface DictionaryResponse {
  Name: string;
  Data?: DictionaryEntry[];
}

/**
 * Hook to fetch api data from dictionary api.
 *
 * Params:
 *   name: <string>  Name of dictionary to retrieve GroupTypes | EventTypes | etc
 */
export function useDictionary(name: string) {
  const { callApi, isAuthenticated } = usePymbleApi();
  const queryClient = useQueryClient();

  // Check for available fields and sort on that if found
  const sortDictionary = (data: DictionaryEntry[]) => {
    return data.sort(function (a, b) {
      try {
        let nameA = "";
        let nameB = "";
        if (a?.Name && b?.Name) {
          nameA = a.Name.toUpperCase(); // ignore upper and lowercase
          nameB = b.Name.toUpperCase(); // ignore upper and lowercase
        } else if (a?.Value && b?.Value) {
          nameA = a.Value.toUpperCase(); // ignore upper and lowercase
          nameB = b.Value.toUpperCase(); // ignore upper and lowercase
        } else if (a?.Title && b?.Title) {
          nameA = a.Title.toUpperCase(); // ignore upper and lowercase
          nameB = b.Title.toUpperCase(); // ignore upper and lowercase
        }

        // Do the sort
        if (nameA && nameB) {
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
        } else {
          return 0;
        }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_e) {
        /* squash */
      }
      // names must be equal or documents incorrect
      return 0;
    });
  };

  // Process response
  const preProcessDictionary = (res: DictionaryResponse[]) => {
    let data: DictionaryEntry[] = [];
    const entry = _.find(res, { Name: name });
    if (entry?.Data) {
      data = entry.Data;
    }
    return data;
  };

  const { data: dictionary = [], isLoading } = useQuery({
    queryKey: ["dictionary", name],
    queryFn: async () => {
      const res = await callApi("GET", DICTIONARY_END_POINT + name);
      if (res) {
        let dict = preProcessDictionary(res as DictionaryResponse[]);
        dict = sortDictionary(dict);
        return dict;
      }
      return [];
    },
    enabled: isAuthenticated && !!name,
    staleTime: 1000 * 60 * 30, // 30 min — dictionaries rarely change
  });

  const refreshDictionary = () => {
    queryClient.invalidateQueries({ queryKey: ["dictionary", name] });
  };

  return {
    dictionary,
    dictionaryLoaded: !isLoading,
    refreshDictionary,
  };
}
